import { CircularProgress, Typography } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { Button, LoadingModal } from "../../../components";
import { useStore } from "../../../store";
import useStyles from "./styles";
import { useAutomaticOrderReceipt } from "../../../hooks/usePrinter";
import {
  useCreatePaymentVisitor,
  useGetTransaction,
  getPaymentCreationErrorMessage,
} from "../../../api/payment/hooks";
import { CreatePaymentVisitorResponse } from "../../../types";
import { usePixEnabledGuard } from "../../../hooks/usePixEnabledGuard";
import { createCheckoutIdempotencyKey } from "../../../utils/createCheckoutIdempotencyKey";

export const PixTotemPayment: React.FC = () => {
  const checkoutIdempotencyKey = useRef(createCheckoutIdempotencyKey());
  const Styles = useStyles();
  const isPixEnabledOnTotem = usePixEnabledGuard("/visitor-purchase");
  const { userManager, navigation, order } = useStore();
  const [pixCode, setPixCode] = useState<string>("");
  const [transactionId, setTransactionId] = useState<string>("");
  const [modalMessage, setModalMessage] = useState<string>("");
  const [seconds, setSeconds] = useState(300);
  const [isActive, setIsActive] = useState(false);
  const [showBtn, setShowBtn] = useState(false);
  const [creationError, setCreationError] = useState(false);
  const { data: transaction } = useGetTransaction(
    transactionId,
    !!transactionId,
    isActive && seconds > 0,
  );
  useAutomaticOrderReceipt(transaction?.status === "3", transactionId, {
    clientName: order.clientName || "Cliente",
    totalAmount: order.totalAmount,
    items: order.items,
  });
  const [reTryPaymentCall, setReTryPaymentCall] = useState(false);
  const { mutateAsync, isLoading } = useCreatePaymentVisitor();
  const formatTime = () => {
    const minutes = Math.floor(seconds / 60);
    const secondsLeft = seconds % 60;
    return `${minutes}:${secondsLeft < 10 ? "0" : ""}${secondsLeft}`;
  };

  const resetCountdown = (interval: any) => {
    clearInterval(interval);
    setReTryPaymentCall(!reTryPaymentCall);
  };

  const stopTimer = () => {
    setIsActive(false);
  };

  useEffect(() => {
    if (!isPixEnabledOnTotem) return;

    (async () => {
      if (transaction) {
        if (transaction.status === "3") {
          setIsActive(false);

          order.reset();

          setModalMessage("Compra Finalizada");

          setTimeout(() => {
            navigation.navigateTo("/login-user");

            setModalMessage("");
          }, 3000);
        }
        if (transaction.status === "6") {
          setIsActive(false);

          order.reset();

          setShowBtn(true);
          setModalMessage("Falha ao finalizar sua compra. tente novamente");
        }
      }
    })();
  }, [transaction]);

  useEffect(() => {
    (async () => {
      try {
        const response: CreatePaymentVisitorResponse = await mutateAsync({
          checkoutIdempotencyKey: checkoutIdempotencyKey.current,
          amount: order.totalAmount,
          entityId: userManager.entity,
          visitorName: order.clientName ?? "Totem",
          paymentMethod: "6",
          isSelfCheckoutTotem: true,
          isOnCredit: order.isOnCredit || false,
          clientName: order.clientName
            ? order.clientName.trim() || ""
            : order.clientName || "",
          items: order.items.map((item) => {
            const {
              id, name, amount, price, totalAmount, observation,
            } = item;

            return {
              id,
              name,
              quantity: amount,
              price,
              totalAmount,
              observation,
            };
          }),
        });

        setTransactionId(response.id);
        setPixCode(response.qrCodeKey);
        setIsActive(true);
        setSeconds(300);
      } catch (error) {
        setIsActive(false);
        setTransactionId("");
        setPixCode("");
        setCreationError(true);
        setShowBtn(true);
        setModalMessage(getPaymentCreationErrorMessage(error));
      }
    })();
  }, [reTryPaymentCall, isPixEnabledOnTotem]);

  useEffect(() => {
    let interval: any = null;

    if (isActive && seconds > 0) {
      interval = setInterval(() => {
        setSeconds((s: number) => s - 1);
      }, 1000);
    } else if (!isActive && seconds !== 0) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [isActive, seconds]);

  useEffect(() => {
    (async () => {
      if (transaction) {
        if (transaction.status === "3") {
          setPixCode("");
          stopTimer();
        }
        if (transaction.status !== "3" && seconds === 0) {
          setPixCode("");
          stopTimer();
        }
      }
    })();
  }, [transaction, seconds]);

  const handleClose = () => {
    navigation.navigateTo("/login-user");
    setModalMessage("");
  };

  const handleBack = () => {
    navigation.navigateTo("/confirm-manual-purchase", {
      state: {
        type: "visitor",
      },
    });
  };

  return (
    <div className={Styles.container}>
      <div className={Styles.containerRelative}>
        <div className={Styles.formView}>
          <Typography className={Styles.title}>
            {`Pagamento Pix - ${order.clientName}`}
          </Typography>

          <div className={Styles.PixContainer}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              pixCode && <QRCode value={pixCode} size={350} />
            )}
          </div>

          <div className={Styles.actions}>
            <Typography className={Styles.subTitle}>
              {`Gerar Novamente em ${formatTime()}`}
            </Typography>
            <Button
              size="large"
              fullWidth
              disabled={seconds !== 0}
              onClick={resetCountdown}
            >
              Gerar Novamente
            </Button>
            <div className={Styles.spacer18} />

            <Button
              color="secondary"
              size="large"
              fullWidth
              onClick={() => handleBack()}
            >
              Voltar
            </Button>
          </div>
        </div>

        <LoadingModal
          open={!!modalMessage}
          message={modalMessage}
          showButton={showBtn}
          onClick={() => (creationError ? handleBack() : handleClose())}
        />
      </div>
    </div>
  );
};
